import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Tabs as MaterialTabs,
  Tab as MaterialTab,
  Box
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';

const sweepLoadingBorder = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const TabsContainer = styled(Box)`
  margin-top: ${p => p.theme.spacing(0)}px;
  margin-bottom: ${p => p.theme.spacing(1)}px;
  overflow-x: auto;

  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const TabsSurface = styled(Box)<{ $loading: boolean }>`
  position: relative;
  display: inline-flex;
  overflow: hidden;
  border-radius: ${p => p.theme.spacing(1.5)}px;
  padding: 2px;
  background: ${p => p.theme.palette.divider};
  box-shadow: ${p =>
    p.$loading ? '0 8px 22px rgba(66, 133, 244, 0.12)' : 'none'};
  transition: box-shadow 240ms ease;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1500px;
    height: 1500px;
    margin-top: -750px;
    margin-left: -750px;
    background: conic-gradient(
      from 0deg,
      rgba(22, 28, 36, 0) 0deg,
      rgba(22, 28, 36, 0) 248deg,
      rgba(49, 130, 206, 0.12) 266deg,
      rgba(49, 130, 206, 0.72) 278deg,
      rgba(56, 189, 248, 0.92) 288deg,
      rgba(45, 212, 191, 0.88) 300deg,
      rgba(129, 140, 248, 0.68) 314deg,
      rgba(22, 28, 36, 0.1) 328deg,
      rgba(22, 28, 36, 0) 344deg,
      rgba(22, 28, 36, 0) 360deg
    );
    opacity: ${p => (p.$loading ? 0.94 : 0)};
    animation: ${sweepLoadingBorder} 3.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    animation-play-state: ${p => (p.$loading ? 'running' : 'paused')};
    transition: opacity 240ms ease;
    pointer-events: none;
    filter: saturate(1.08);
    transform-origin: center;
    will-change: transform;
  }
`;

const TabsSurfaceInner = styled(Box)`
  position: relative;
  z-index: 1;
  display: inline-flex;
  border-radius: calc(${p => p.theme.spacing(1.5)}px - 2px);
  background: ${p => p.theme.palette.grey['100']};
`;

const StyledTabs = styled(MaterialTabs)`
  min-height: 0;

  .MuiTabs-flexContainer {
    display: inline-flex;
    gap: ${p => p.theme.spacing(1)}px;
    padding: ${p => p.theme.spacing(0.75)}px;
  }

  .MuiTabs-scroller {
    overflow: visible !important;
  }

  .MuiTabs-indicator {
    display: none;
  }
`;

const StyledTab = styled(MaterialTab)`
  && {
    min-width: 0;
    min-height: 0;
    padding: ${p => p.theme.spacing(1, 1.75)};
    border-radius: ${p => p.theme.spacing(1)}px;
    text-transform: none;
    letter-spacing: 0;
    font-size: 0.9rem;
    font-weight: 500;
    color: ${p => p.theme.palette.text.secondary};
    opacity: 1;
    transition: background-color 160ms ease, color 160ms ease,
      box-shadow 160ms ease;
  }

  &&:hover {
    background: ${p => p.theme.palette.action.hover};
  }

  &&.Mui-selected {
    color: ${p => p.theme.palette.primary.main};
    background: ${p => p.theme.palette.background.paper};
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.14);
  }

  .MuiTab-wrapper {
    flex-direction: row;
  }

  .MuiTab-wrapper > *:first-child {
    margin-bottom: 0;
  }
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${p => p.theme.spacing(0.75)}px;
`;

export interface TabParams {
  tab?: string;
}

export interface TabOption {
  label?: React.ReactNode;
  icon?: JSX.Element;
  ariaLabel?: string;
}

interface Props {
  selected?: string;
  tabs: { [k: string]: JSX.Element };
  tabOptions?: { [k: string]: TabOption };
  onTabChange?: (tab: string) => unknown;
  loading?: boolean;
}

function Tabs({ selected, tabs, tabOptions, onTabChange, loading }: Props) {
  if (Object.keys(tabs).length === 0) {
    throw new Error('Must specify more than 0 tabs');
  }
  const { t } = useTranslation();
  const normalizeTabText = (label: string) =>
    label
      .split(' ')
      .map(word =>
        word.length ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word
      )
      .join(' ');
  const getTabLabel = (tabName: string) => {
    const translation = t(tabName);

    const rawLabel =
      translation === tabName
        ? tabName.split(/(?=[A-Z])/).join(' ')
        : translation;

    return typeof rawLabel === 'string' ? normalizeTabText(rawLabel) : rawLabel;
  };
  const renderTabLabel = (tabName: string) => {
    const label = tabOptions?.[tabName]?.label ?? getTabLabel(tabName);
    const icon = tabOptions?.[tabName]?.icon;
    const normalizedLabel =
      typeof label === 'string' ? normalizeTabText(label) : label;

    if (!icon) {
      return normalizedLabel;
    }

    return (
      <TabLabel>
        {icon}
        <span>{normalizedLabel}</span>
      </TabLabel>
    );
  };

  const [activeTab, setActiveTab] = useState<string>(
    selected || Object.keys(tabs)[0]
  );

  useEffect(() => {
    const firstTab = Object.keys(tabs)[0];

    if (selected && !tabs[selected]) {
      setActiveTab(firstTab);
      onTabChange && onTabChange(firstTab);
      return;
    }

    setActiveTab(currentActiveTab => {
      if (selected && tabs[selected]) {
        return selected;
      }

      return tabs[currentActiveTab] ? currentActiveTab : firstTab;
    });
  }, [onTabChange, selected, tabs]);

  const handleTabChange = (event: unknown, selectedTab: string) => {
    setActiveTab(selectedTab);
    onTabChange && onTabChange(selectedTab);
  };

  return (
    <>
      <TabsContainer>
        <TabsSurface $loading={loading ?? false}>
          <TabsSurfaceInner>
            <StyledTabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              variant="scrollable"
              textColor="primary"
              scrollButtons="auto"
            >
              {Object.keys(tabs).map(tabName => (
                <StyledTab
                  key={tabName}
                  aria-label={tabOptions?.[tabName]?.ariaLabel}
                  label={renderTabLabel(tabName)}
                  value={tabName}
                />
              ))}
            </StyledTabs>
          </TabsSurfaceInner>
        </TabsSurface>
      </TabsContainer>
      {tabs[activeTab]}
    </>
  );
}

export { Tabs };
